"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ApiWaveCuration } from "@/generated/models/ApiWaveCuration";
import { useWave } from "@/hooks/useWave";
import type { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import { AnimatePresence, motion } from "framer-motion";
import { AdjustmentsHorizontalIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { PlusIcon } from "@heroicons/react/24/solid";
import React, { useCallback, useContext, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { Tooltip } from "react-tooltip";
import { useDebounce } from "react-use";
import { getWaveDropEligibility } from "../dropEligibility";
import type { LeaderboardViewMode } from "../types";
import { WaveLeaderboardCurationGroupSelect } from "./WaveLeaderboardCurationGroupSelect";
import { useLeaderboardHeaderControlMeasurements } from "./useLeaderboardHeaderControlMeasurements";
import { WAVE_LEADERBOARD_CURATION_SORT_ITEMS, WAVE_LEADERBOARD_SORT_ITEMS, WaveleaderboardSort } from "./WaveleaderboardSort";
import { resolveWaveLeaderboardHeaderLayout } from "./waveLeaderboardHeaderLayout";
interface WaveLeaderboardHeaderProps {
  readonly wave: ApiWave;
  readonly onCreateDrop?: (() => void) | undefined;
  readonly viewMode: LeaderboardViewMode;
  readonly onViewModeChange: (mode: LeaderboardViewMode) => void;
  readonly sort: WaveDropsLeaderboardSort;
  readonly onSortChange: (sort: WaveDropsLeaderboardSort) => void;
  readonly curationGroups?: readonly ApiWaveCuration[] | undefined;
  readonly curatedByGroupId?: string | null | undefined;
  readonly onCurationGroupChange?: ((groupId: string | null) => void) | undefined;
  readonly minPrice?: number | undefined;
  readonly maxPrice?: number | undefined;
  readonly onPriceRangeChange?: ((values: {
    readonly minPrice: number | undefined;
    readonly maxPrice: number | undefined;
  }) => void) | undefined;
}
interface WaveLeaderboardPriceFiltersProps {
  readonly waveId: string;
  readonly minPrice?: number | undefined;
  readonly maxPrice?: number | undefined;
  readonly onPriceRangeChange?: ((values: {
    readonly minPrice: number | undefined;
    readonly maxPrice: number | undefined;
  }) => void) | undefined;
  readonly onFiltersActivated: () => void;
  readonly onFiltersCleared: () => void;
  readonly trailingActions?: React.ReactNode | undefined;
}
const toPriceInputValue = (value?: number): string => typeof value === "number" ? value.toString() : "";
const parsePriceInput = (rawValue: string): number | undefined => {
  if (!rawValue.trim()) {
    return undefined;
  }
  const numericValue = Number.parseFloat(rawValue);
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return undefined;
  }
  return numericValue;
};
const isZeroDraftInput = (rawValue: string): boolean => {
  const trimmedValue = rawValue.trim();
  return trimmedValue === "0" || trimmedValue === "0.";
};
const PRICE_FILTER_LONG_PLACEHOLDER_WIDTH = 576;
const WaveLeaderboardPriceFilters: React.FC<WaveLeaderboardPriceFiltersProps> = t0 => {
  const $ = _c(54);
  const {
    waveId,
    minPrice,
    maxPrice,
    onPriceRangeChange,
    onFiltersActivated,
    onFiltersCleared,
    trailingActions
  } = t0;
  let t1;
  if ($[0] !== minPrice) {
    t1 = () => toPriceInputValue(minPrice);
    $[0] = minPrice;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [minPriceInput, setMinPriceInput] = useState(t1);
  let t2;
  if ($[2] !== maxPrice) {
    t2 = () => toPriceInputValue(maxPrice);
    $[2] = maxPrice;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [maxPriceInput, setMaxPriceInput] = useState(t2);
  const hasDraftInputEditsRef = useRef(false);
  const priceFiltersContainerRef = useRef(null);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = onStoreChange => {
      const container = priceFiltersContainerRef.current;
      const notify = () => onStoreChange();
      notify();
      if (typeof ResizeObserver !== "undefined" && container) {
        const resizeObserver = new ResizeObserver(() => {
          notify();
        });
        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
      }
      if (typeof window === "undefined") {
        return _temp;
      }
      window.addEventListener("resize", notify);
      return () => window.removeEventListener("resize", notify);
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const subscribePlaceholderMode = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const container_0 = priceFiltersContainerRef.current;
      if (!container_0) {
        return false;
      }
      return container_0.getBoundingClientRect().width >= PRICE_FILTER_LONG_PLACEHOLDER_WIDTH;
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const getPlaceholderSnapshot = t4;
  const useLongPlaceholders = useSyncExternalStore(subscribePlaceholderMode, getPlaceholderSnapshot, _temp2);
  let t5;
  if ($[6] !== maxPrice || $[7] !== maxPriceInput || $[8] !== minPrice || $[9] !== minPriceInput || $[10] !== onFiltersActivated || $[11] !== onPriceRangeChange) {
    t5 = () => {
      if (!onPriceRangeChange) {
        return;
      }
      const nextMinPrice = parsePriceInput(minPriceInput);
      const nextMaxPrice = parsePriceInput(maxPriceInput);
      const hasSameCommittedRange = nextMinPrice === minPrice && nextMaxPrice === maxPrice;
      if (hasSameCommittedRange) {
        return;
      }
      const hasActivePriceFilters = typeof nextMinPrice === "number" || typeof nextMaxPrice === "number";
      if (hasActivePriceFilters) {
        onFiltersActivated();
      }
      onPriceRangeChange({
        minPrice: nextMinPrice,
        maxPrice: nextMaxPrice
      });
    };
    $[6] = maxPrice;
    $[7] = maxPriceInput;
    $[8] = minPrice;
    $[9] = minPriceInput;
    $[10] = onFiltersActivated;
    $[11] = onPriceRangeChange;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const commitPriceRange = t5;
  let t6;
  if ($[13] !== commitPriceRange || $[14] !== maxPriceInput || $[15] !== minPriceInput) {
    t6 = () => {
      if (!hasDraftInputEditsRef.current) {
        return;
      }
      if (isZeroDraftInput(minPriceInput) || isZeroDraftInput(maxPriceInput)) {
        return;
      }
      commitPriceRange();
    };
    $[13] = commitPriceRange;
    $[14] = maxPriceInput;
    $[15] = minPriceInput;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== maxPrice || $[18] !== maxPriceInput || $[19] !== minPrice || $[20] !== minPriceInput || $[21] !== onFiltersActivated || $[22] !== onPriceRangeChange) {
    t7 = [minPriceInput, maxPriceInput, minPrice, maxPrice, onPriceRangeChange, onFiltersActivated];
    $[17] = maxPrice;
    $[18] = maxPriceInput;
    $[19] = minPrice;
    $[20] = minPriceInput;
    $[21] = onFiltersActivated;
    $[22] = onPriceRangeChange;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  useDebounce(t6, 350, t7);
  let t8;
  if ($[24] !== maxPriceInput || $[25] !== minPriceInput) {
    t8 = minPriceInput.trim().length > 0 || maxPriceInput.trim().length > 0;
    $[24] = maxPriceInput;
    $[25] = minPriceInput;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const hasAnyPriceInput = t8;
  const t9 = `leaderboard-min-price-${waveId}`;
  const t10 = useLongPlaceholders ? "Minimum ETH" : "Min";
  let t11;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = event => {
      hasDraftInputEditsRef.current = true;
      setMinPriceInput(event.target.value);
    };
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== commitPriceRange) {
    t12 = event_0 => {
      if (event_0.key === "Enter") {
        commitPriceRange();
      }
    };
    $[28] = commitPriceRange;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== commitPriceRange || $[31] !== minPriceInput || $[32] !== t10 || $[33] !== t12 || $[34] !== t9) {
    t13 = <div className="tw-min-w-[6.5rem] tw-flex-1 @[28rem]/price:tw-min-w-[8rem] @[36rem]/price:tw-min-w-[9.5rem]"><input id={t9} data-testid="leaderboard-price-min-input" type="number" min={0} step="any" inputMode="decimal" aria-label="Minimum ETH" title="Minimum ETH" placeholder={t10} value={minPriceInput} onChange={t11} onBlur={commitPriceRange} onKeyDown={t12} className="tw-form-input tw-block tw-h-9 tw-w-full tw-appearance-none tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-px-3 tw-text-sm tw-font-normal tw-text-iron-50 tw-caret-primary-400 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out [appearance:textfield] placeholder:tw-text-iron-400 hover:tw-ring-iron-600 focus:tw-bg-transparent focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 [&::-webkit-inner-spin-button]:tw-appearance-none [&::-webkit-outer-spin-button]:tw-appearance-none" /></div>;
    $[30] = commitPriceRange;
    $[31] = minPriceInput;
    $[32] = t10;
    $[33] = t12;
    $[34] = t9;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const t14 = `leaderboard-max-price-${waveId}`;
  const t15 = useLongPlaceholders ? "Maximum ETH" : "Max";
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = event_1 => {
      hasDraftInputEditsRef.current = true;
      setMaxPriceInput(event_1.target.value);
    };
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== commitPriceRange) {
    t17 = event_2 => {
      if (event_2.key === "Enter") {
        commitPriceRange();
      }
    };
    $[37] = commitPriceRange;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== commitPriceRange || $[40] !== maxPriceInput || $[41] !== t14 || $[42] !== t15 || $[43] !== t17) {
    t18 = <div className="tw-min-w-[6.5rem] tw-flex-1 @[28rem]/price:tw-min-w-[8rem] @[36rem]/price:tw-min-w-[9.5rem]"><input id={t14} data-testid="leaderboard-price-max-input" type="number" min={0} step="any" inputMode="decimal" aria-label="Maximum ETH" title="Maximum ETH" placeholder={t15} value={maxPriceInput} onChange={t16} onBlur={commitPriceRange} onKeyDown={t17} className="tw-form-input tw-block tw-h-9 tw-w-full tw-appearance-none tw-rounded-lg tw-border-0 tw-bg-iron-900 tw-px-3 tw-text-sm tw-font-normal tw-text-iron-50 tw-caret-primary-400 tw-shadow-sm tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition tw-duration-300 tw-ease-out [appearance:textfield] placeholder:tw-text-iron-400 hover:tw-ring-iron-600 focus:tw-bg-transparent focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset focus:tw-ring-primary-400 [&::-webkit-inner-spin-button]:tw-appearance-none [&::-webkit-outer-spin-button]:tw-appearance-none" /></div>;
    $[39] = commitPriceRange;
    $[40] = maxPriceInput;
    $[41] = t14;
    $[42] = t15;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== hasAnyPriceInput || $[46] !== onFiltersCleared || $[47] !== onPriceRangeChange) {
    t19 = hasAnyPriceInput && <button type="button" data-testid="leaderboard-price-clear" aria-label="Clear filters" title="Clear filters" onClick={() => {
      setMinPriceInput("");
      setMaxPriceInput("");
      onFiltersCleared();
      onPriceRangeChange?.({
        minPrice: undefined,
        maxPrice: undefined
      });
    }} className="tw-inline-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-text-iron-300 tw-transition tw-duration-300 tw-ease-out focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400 desktop-hover:hover:tw-border-white/15 desktop-hover:hover:tw-bg-white/5 desktop-hover:hover:tw-text-iron-100"><XMarkIcon className="tw-h-4 tw-w-4" aria-hidden="true" /></button>;
    $[45] = hasAnyPriceInput;
    $[46] = onFiltersCleared;
    $[47] = onPriceRangeChange;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t13 || $[50] !== t18 || $[51] !== t19 || $[52] !== trailingActions) {
    t20 = <div ref={priceFiltersContainerRef} className="tw-flex tw-flex-nowrap tw-items-center tw-gap-2 tw-overflow-x-auto tw-@container/price">{t13}{t18}{t19}{trailingActions}</div>;
    $[49] = t13;
    $[50] = t18;
    $[51] = t19;
    $[52] = trailingActions;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  return t20;
};
const DropModeGlyphIcon: React.FC<{
  readonly className?: string | undefined;
}> = t0 => {
  const $ = _c(5);
  const {
    className: t1
  } = t0;
  const className = t1 === undefined ? "tw-size-4 tw-flex-shrink-0" : t1;
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <path d="M8.62826 7.89684C8.62826 7.60735 8.62826 6.72633 7.11906 4.34441C6.41025 3.22565 5.71213 2.3144 5.68274 2.27615L5.12514 1.55005L4.56755 2.27615C4.53816 2.3144 3.84008 3.2257 3.13123 4.34441C1.62207 6.72633 1.62207 7.60735 1.62207 7.89684C1.62207 9.82846 3.19352 11.3999 5.12514 11.3999C7.05676 11.3999 8.62826 9.82846 8.62826 7.89684Z" fill="currentColor" />;
    t3 = <path d="M21.2502 2.24459C20.7301 1.42366 20.2173 0.754227 20.1956 0.726104L19.638 0L19.0805 0.726104C19.0589 0.754227 18.546 1.42366 18.0259 2.24459C17.5419 3.00847 16.8984 4.11804 16.8984 4.931C16.8984 6.44166 18.1274 7.67061 19.638 7.67061C21.1487 7.67061 22.3777 6.44161 22.3777 4.931C22.3777 4.11799 21.7342 3.00847 21.2502 2.24459Z" fill="currentColor" />;
    t4 = <path d="M13.6806 7.0994L13.1231 6.37329L12.5655 7.0994C12.5083 7.17388 11.1491 8.94805 9.76692 11.1295C7.8616 14.1367 6.89551 16.3717 6.89551 17.7724C6.89551 21.2063 9.68921 24 13.1231 24C16.557 24 19.3506 21.2063 19.3506 17.7724C19.3506 16.3717 18.3845 14.1367 16.4792 11.1295C15.097 8.94805 13.7379 7.17388 13.6806 7.0994Z" fill="currentColor" />;
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t2 = $[0];
    t3 = $[1];
    t4 = $[2];
  }
  let t5;
  if ($[3] !== className) {
    t5 = <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">{t2}{t3}{t4}</svg>;
    $[3] = className;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  return t5;
};
export const WaveLeaderboardHeader: React.FC<WaveLeaderboardHeaderProps> = t0 => {
  const $ = _c(129);
  const {
    wave,
    onCreateDrop,
    viewMode: t1,
    onViewModeChange,
    sort,
    onSortChange,
    curationGroups: t2,
    curatedByGroupId: t3,
    onCurationGroupChange,
    minPrice,
    maxPrice,
    onPriceRangeChange
  } = t0;
  const viewMode = t1 === undefined ? "list" : t1;
  let t4;
  if ($[0] !== t2) {
    t4 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const curationGroups = t4;
  const curatedByGroupId = t3 === undefined ? null : t3;
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    isMemesWave,
    isCurationWave,
    isQuorumWave,
    participation
  } = useWave(wave);
  const isLoggedIn = Boolean(connectedProfile?.handle);
  const t5 = Boolean(activeProfileProxy);
  let t6;
  if ($[2] !== isCurationWave || $[3] !== isLoggedIn || $[4] !== participation || $[5] !== t5) {
    t6 = getWaveDropEligibility({
      isLoggedIn,
      isProxy: t5,
      isCurationWave,
      participation
    });
    $[2] = isCurationWave;
    $[3] = isLoggedIn;
    $[4] = participation;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const {
    canCreateDrop
  } = t6;
  const showCurationGroupSelect = Boolean(onCurationGroupChange && curationGroups.length > 0);
  const showPriceControls = Boolean(isCurationWave && onPriceRangeChange);
  const sortItems = isCurationWave ? WAVE_LEADERBOARD_CURATION_SORT_ITEMS : WAVE_LEADERBOARD_SORT_ITEMS;
  const viewModes = isMemesWave ? ["list", "grid"] : ["list", "grid", "grid_content_only"];
  const hasActivePriceFilters = typeof minPrice === "number" || typeof maxPrice === "number";
  const [isManualFiltersOpen, setIsManualFiltersOpen] = useState(false);
  const [isActiveFiltersCollapsed, setIsActiveFiltersCollapsed] = useState(false);
  const isPriceFiltersOpen = hasActivePriceFilters ? !isActiveFiltersCollapsed : isManualFiltersOpen;
  let t7;
  if ($[7] !== sortItems) {
    t7 = new Map(sortItems.map(_temp3));
    $[7] = sortItems;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const sortLabelByValue = t7;
  let t8;
  if ($[9] !== sort || $[10] !== sortLabelByValue) {
    t8 = sortLabelByValue.get(sort) ?? "Current Vote";
    $[9] = sort;
    $[10] = sortLabelByValue;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const activeSortLabel = t8;
  let t9;
  bb0: {
    if (!showCurationGroupSelect || !curatedByGroupId) {
      t9 = "All submissions";
      break bb0;
    }
    let t10;
    if ($[12] !== curatedByGroupId || $[13] !== curationGroups) {
      t10 = curationGroups.find(group => group.id === curatedByGroupId)?.name ?? "All submissions";
      $[12] = curatedByGroupId;
      $[13] = curationGroups;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    t9 = t10;
  }
  const activeCurationLabel = t9;
  let t10;
  if ($[15] !== curationGroups) {
    t10 = curationGroups.map(_temp4).join("|");
    $[15] = curationGroups;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const curationProbeKey = t10;
  const showCurationActions = showPriceControls;
  const showCreateAction = Boolean(isLoggedIn && canCreateDrop && onCreateDrop);
  let actionsRemeasureVariant = "none";
  if (showCurationActions && showCreateAction) {
    actionsRemeasureVariant = "with-drop";
  } else {
    if (showCurationActions) {
      actionsRemeasureVariant = "filter-only";
    }
  }
  const remeasureKey = `${activeSortLabel}|${activeCurationLabel}|${curationProbeKey}|actions:${actionsRemeasureVariant}`;
  let t11;
  if ($[17] !== remeasureKey || $[18] !== showCurationActions || $[19] !== showCurationGroupSelect) {
    t11 = {
      showCurationGroupSelect,
      measureAgainstHeaderRow: showCurationActions,
      remeasureKey
    };
    $[17] = remeasureKey;
    $[18] = showCurationActions;
    $[19] = showCurationGroupSelect;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const {
    headerRowRef,
    controlsRowRef,
    viewModeTabsRef,
    sortTabsProbeRef,
    sortDropdownProbeRef,
    curationTabsProbeRef,
    curationDropdownProbeRef,
    actionsFullProbeRef,
    actionsIconProbeRef,
    measurements
  } = useLeaderboardHeaderControlMeasurements(t11);
  let t12;
  if ($[21] !== measurements.actionsFullWidth || $[22] !== measurements.actionsIconWidth || $[23] !== measurements.curationDropdownWidth || $[24] !== measurements.curationTabsWidth || $[25] !== measurements.rowWidth || $[26] !== measurements.sortDropdownWidth || $[27] !== measurements.sortTabsWidth || $[28] !== measurements.viewModesWidth || $[29] !== showCurationActions || $[30] !== showCurationGroupSelect) {
    t12 = resolveWaveLeaderboardHeaderLayout({
      rowWidth: measurements.rowWidth,
      viewModesWidth: measurements.viewModesWidth,
      sortTabsWidth: measurements.sortTabsWidth,
      sortDropdownWidth: measurements.sortDropdownWidth,
      hasCurationControl: showCurationGroupSelect,
      curationTabsWidth: measurements.curationTabsWidth,
      curationDropdownWidth: measurements.curationDropdownWidth,
      hasActions: showCurationActions,
      actionsFullWidth: measurements.actionsFullWidth,
      actionsIconWidth: measurements.actionsIconWidth,
      wrapEarlyThresholdPx: 20
    });
    $[21] = measurements.actionsFullWidth;
    $[22] = measurements.actionsIconWidth;
    $[23] = measurements.curationDropdownWidth;
    $[24] = measurements.curationTabsWidth;
    $[25] = measurements.rowWidth;
    $[26] = measurements.sortDropdownWidth;
    $[27] = measurements.sortTabsWidth;
    $[28] = measurements.viewModesWidth;
    $[29] = showCurationActions;
    $[30] = showCurationGroupSelect;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const layout = t12;
  const getViewModeLabel = _temp5;
  const getViewModeTabClass = _temp6;
  const getViewModeIcon = _temp7;
  let t13;
  if ($[32] !== hasActivePriceFilters || $[33] !== setIsActiveFiltersCollapsed || $[34] !== setIsManualFiltersOpen) {
    t13 = () => {
      if (hasActivePriceFilters) {
        setIsActiveFiltersCollapsed(_temp8);
        return;
      }
      setIsManualFiltersOpen(_temp9);
    };
    $[32] = hasActivePriceFilters;
    $[33] = setIsActiveFiltersCollapsed;
    $[34] = setIsManualFiltersOpen;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const onTogglePriceFilters = t13;
  const isCompactActions = layout.actionMode === "icon";
  const shouldRenderActionsInPriceRow = showCurationActions && isPriceFiltersOpen && layout.wrapActions;
  let headerRowFlexClass = "tw-flex-wrap";
  if (showCurationActions) {
    if (shouldRenderActionsInPriceRow) {
      headerRowFlexClass = "tw-flex-nowrap";
    } else {
      headerRowFlexClass = layout.wrapActions ? "tw-flex-wrap" : "tw-flex-nowrap";
    }
  }
  const controlsRowBasisClass = layout.wrapActions && !shouldRenderActionsInPriceRow ? "tw-basis-full" : "";
  const showHeaderActions = showCurationActions && !shouldRenderActionsInPriceRow;
  const showDefaultCreateRow = !showCurationActions && isLoggedIn;
  const defaultCreateLabel = isQuorumWave ? "Create Proposal" : "Drop";
  const t14 = `leaderboard-price-panel-${wave.id}`;
  const t15 = `tw-inline-flex tw-h-9 tw-items-center tw-rounded-lg tw-border tw-border-solid tw-text-xs tw-font-semibold tw-transition ${isCompactActions ? "tw-w-9 tw-justify-center tw-px-0" : "tw-gap-2 tw-px-3.5"} ${isPriceFiltersOpen || hasActivePriceFilters ? "tw-border-white/15 tw-bg-white/10 tw-text-iron-100" : "tw-border-white/10 tw-bg-iron-950 tw-text-iron-200 desktop-hover:hover:tw-border-white/15 desktop-hover:hover:tw-bg-white/5"}`;
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <AdjustmentsHorizontalIcon className="tw-size-4 tw-flex-shrink-0" />;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== isCompactActions) {
    t17 = isCompactActions ? <span className="tw-sr-only">Filters</span> : <span>Filters</span>;
    $[37] = isCompactActions;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== isPriceFiltersOpen || $[40] !== onTogglePriceFilters || $[41] !== t14 || $[42] !== t15 || $[43] !== t17) {
    t18 = <button type="button" data-testid="leaderboard-price-toggle" aria-label="Toggle filters" aria-expanded={isPriceFiltersOpen} aria-controls={t14} onClick={onTogglePriceFilters} className={t15}>{t16}{t17}</button>;
    $[39] = isPriceFiltersOpen;
    $[40] = onTogglePriceFilters;
    $[41] = t14;
    $[42] = t15;
    $[43] = t17;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== isCompactActions || $[46] !== onCreateDrop || $[47] !== showCreateAction) {
    t19 = showCreateAction && <PrimaryButton loading={false} disabled={false} onClicked={() => onCreateDrop?.()} padding={isCompactActions ? "tw-px-2.5 tw-py-2" : "tw-px-3.5 tw-py-2"}>{isCompactActions ? <><DropModeGlyphIcon /><span className="tw-sr-only">Drop Art</span></> : <><PlusIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /><span>Drop Art</span></>}</PrimaryButton>;
    $[45] = isCompactActions;
    $[46] = onCreateDrop;
    $[47] = showCreateAction;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t18 || $[50] !== t19) {
    t20 = <>{t18}{t19}</>;
    $[49] = t18;
    $[50] = t19;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  const curationActionControls = t20;
  const t21 = "tw-relative tw-flex tw-flex-col tw-gap-y-4 tw-bg-black tw-@container";
  const t22 = "leaderboard-header-row";
  const t23 = `tw-flex tw-items-start tw-gap-2 ${headerRowFlexClass}`;
  const t24 = "leaderboard-header-controls-row";
  const t25 = `tw-flex tw-min-w-0 tw-flex-1 tw-flex-nowrap tw-items-center tw-gap-2 ${controlsRowBasisClass} ${layout.enableControlsScroll ? "horizontal-menu-hide-scrollbar tw-overflow-x-auto tw-scrollbar-thin tw-scrollbar-track-transparent tw-scrollbar-thumb-iron-700/60" : "tw-overflow-x-hidden"}`;
  const t26 = "tablist";
  const t27 = "Leaderboard view modes";
  const t28 = "tw-flex tw-flex-shrink-0 tw-gap-0.5 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-p-1";
  const t29 = viewModes.map(mode_2 => <React.Fragment key={mode_2}><button type="button" role="tab" aria-label={getViewModeLabel(mode_2)} aria-selected={viewMode === mode_2} tabIndex={viewMode === mode_2 ? 0 : -1} className={getViewModeTabClass(mode_2, viewMode)} onClick={() => onViewModeChange(mode_2)} data-tooltip-id={`leaderboard-view-mode-${mode_2}-${wave.id}`}>{getViewModeIcon(mode_2)}<span className="tw-sr-only">{getViewModeLabel(mode_2)}</span></button><Tooltip id={`leaderboard-view-mode-${mode_2}-${wave.id}`} place="top" offset={8} opacity={1} style={{
      padding: "4px 8px",
      background: "#37373E",
      color: "white",
      fontSize: "13px",
      fontWeight: 500,
      borderRadius: "6px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      zIndex: 99999,
      pointerEvents: "none"
    }}>{getViewModeLabel(mode_2)}</Tooltip></React.Fragment>);
  let t30;
  if ($[52] !== t29 || $[53] !== viewModeTabsRef) {
    t30 = <div ref={viewModeTabsRef} role={t26} aria-label={t27} className={t28}>{t29}</div>;
    $[52] = t29;
    $[53] = viewModeTabsRef;
    $[54] = t30;
  } else {
    t30 = $[54];
  }
  let t31;
  if ($[55] !== layout.sortMode || $[56] !== onSortChange || $[57] !== sort || $[58] !== sortItems) {
    t31 = <div className="tw-flex-shrink-0"><WaveleaderboardSort sort={sort} onSortChange={onSortChange} mode={layout.sortMode} items={sortItems} /></div>;
    $[55] = layout.sortMode;
    $[56] = onSortChange;
    $[57] = sort;
    $[58] = sortItems;
    $[59] = t31;
  } else {
    t31 = $[59];
  }
  let t32;
  if ($[60] !== curatedByGroupId || $[61] !== curationGroups || $[62] !== layout.curationMode || $[63] !== onCurationGroupChange || $[64] !== showCurationGroupSelect) {
    t32 = showCurationGroupSelect && onCurationGroupChange && <div className="tw-flex-shrink-0"><WaveLeaderboardCurationGroupSelect groups={curationGroups} selectedGroupId={curatedByGroupId} onChange={onCurationGroupChange} mode={layout.curationMode} /></div>;
    $[60] = curatedByGroupId;
    $[61] = curationGroups;
    $[62] = layout.curationMode;
    $[63] = onCurationGroupChange;
    $[64] = showCurationGroupSelect;
    $[65] = t32;
  } else {
    t32 = $[65];
  }
  let t33;
  if ($[66] !== controlsRowRef || $[67] !== t25 || $[68] !== t30 || $[69] !== t31 || $[70] !== t32) {
    t33 = <div ref={controlsRowRef} data-testid={t24} className={t25}>{t30}{t31}{t32}</div>;
    $[66] = controlsRowRef;
    $[67] = t25;
    $[68] = t30;
    $[69] = t31;
    $[70] = t32;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== curationActionControls || $[73] !== layout.actionMode || $[74] !== layout.wrapActions || $[75] !== showHeaderActions) {
    t34 = showHeaderActions && <div data-testid="leaderboard-header-actions-row" data-action-mode={layout.actionMode} data-wrap={layout.wrapActions ? "yes" : "no"} className={`tw-ml-auto tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2 ${layout.wrapActions ? "tw-basis-auto" : ""}`}>{curationActionControls}</div>;
    $[72] = curationActionControls;
    $[73] = layout.actionMode;
    $[74] = layout.wrapActions;
    $[75] = showHeaderActions;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] !== canCreateDrop || $[78] !== defaultCreateLabel || $[79] !== isMemesWave || $[80] !== onCreateDrop || $[81] !== showDefaultCreateRow) {
    t35 = showDefaultCreateRow && <div className={`tw-flex tw-flex-col tw-items-end ${isMemesWave ? "lg:tw-hidden" : ""}`}>{canCreateDrop && onCreateDrop && <PrimaryButton loading={false} disabled={false} onClicked={onCreateDrop} padding="tw-px-3 tw-py-2"><PlusIcon className="-tw-ml-1 tw-h-4 tw-w-4 tw-flex-shrink-0" /><span>{defaultCreateLabel}</span></PrimaryButton>}</div>;
    $[77] = canCreateDrop;
    $[78] = defaultCreateLabel;
    $[79] = isMemesWave;
    $[80] = onCreateDrop;
    $[81] = showDefaultCreateRow;
    $[82] = t35;
  } else {
    t35 = $[82];
  }
  let t36;
  if ($[83] !== headerRowRef || $[84] !== t23 || $[85] !== t33 || $[86] !== t34 || $[87] !== t35) {
    t36 = <div ref={headerRowRef} data-testid={t22} className={t23}>{t33}{t34}{t35}</div>;
    $[83] = headerRowRef;
    $[84] = t23;
    $[85] = t33;
    $[86] = t34;
    $[87] = t35;
    $[88] = t36;
  } else {
    t36 = $[88];
  }
  let t37;
  if ($[89] !== curationActionControls || $[90] !== isPriceFiltersOpen || $[91] !== maxPrice || $[92] !== minPrice || $[93] !== onPriceRangeChange || $[94] !== setIsActiveFiltersCollapsed || $[95] !== setIsManualFiltersOpen || $[96] !== shouldRenderActionsInPriceRow || $[97] !== showPriceControls || $[98] !== wave.id) {
    t37 = showPriceControls && <AnimatePresence initial={false}>{isPriceFiltersOpen && <motion.div id={`leaderboard-price-panel-${wave.id}`} data-testid="leaderboard-price-panel" initial={{
        opacity: 0,
        height: 0,
        y: -8
      }} animate={{
        opacity: 1,
        height: "auto",
        y: 0
      }} exit={{
        opacity: 0,
        height: 0,
        y: -8
      }} transition={{
        duration: 0.2,
        ease: "easeInOut"
      }} className="tw-overflow-hidden"><WaveLeaderboardPriceFilters key={`${minPrice ?? ""}|${maxPrice ?? ""}`} waveId={wave.id} minPrice={minPrice} maxPrice={maxPrice} onPriceRangeChange={onPriceRangeChange} onFiltersActivated={() => setIsActiveFiltersCollapsed(false)} onFiltersCleared={() => {
          setIsManualFiltersOpen(false);
          setIsActiveFiltersCollapsed(false);
        }} trailingActions={shouldRenderActionsInPriceRow ? <div data-testid="leaderboard-price-actions-row" className="tw-ml-auto tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2">{curationActionControls}</div> : undefined} /></motion.div>}</AnimatePresence>;
    $[89] = curationActionControls;
    $[90] = isPriceFiltersOpen;
    $[91] = maxPrice;
    $[92] = minPrice;
    $[93] = onPriceRangeChange;
    $[94] = setIsActiveFiltersCollapsed;
    $[95] = setIsManualFiltersOpen;
    $[96] = shouldRenderActionsInPriceRow;
    $[97] = showPriceControls;
    $[98] = wave.id;
    $[99] = t37;
  } else {
    t37 = $[99];
  }
  let t38;
  if ($[100] !== sortItems) {
    t38 = sortItems.map(_temp10);
    $[100] = sortItems;
    $[101] = t38;
  } else {
    t38 = $[101];
  }
  let t39;
  if ($[102] !== sortTabsProbeRef || $[103] !== t38) {
    t39 = <div ref={sortTabsProbeRef} className="tw-inline-flex tw-items-center tw-gap-x-1 tw-rounded-lg tw-bg-iron-950 tw-p-1 tw-ring-1 tw-ring-inset tw-ring-iron-700">{t38}</div>;
    $[102] = sortTabsProbeRef;
    $[103] = t38;
    $[104] = t39;
  } else {
    t39 = $[104];
  }
  let t40;
  if ($[105] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <span className="tw-font-semibold tw-text-iron-500">Sort: </span>;
    $[105] = t40;
  } else {
    t40 = $[105];
  }
  let t41;
  if ($[106] !== activeSortLabel || $[107] !== sortDropdownProbeRef) {
    t41 = <div ref={sortDropdownProbeRef} className="tailwind-scope tw-inline-flex tw-whitespace-nowrap tw-rounded-lg tw-py-2.5 tw-pl-3.5 tw-pr-8 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset tw-ring-iron-700">{t40}{activeSortLabel}</div>;
    $[106] = activeSortLabel;
    $[107] = sortDropdownProbeRef;
    $[108] = t41;
  } else {
    t41 = $[108];
  }
  let t42;
  if ($[109] !== activeCurationLabel || $[110] !== curationDropdownProbeRef || $[111] !== curationGroups || $[112] !== curationTabsProbeRef || $[113] !== showCurationGroupSelect) {
    t42 = showCurationGroupSelect && <><div ref={curationTabsProbeRef} className="tw-inline-flex tw-items-center tw-gap-x-1"><div className="tw-flex tw-items-center tw-gap-x-1.5 tw-whitespace-nowrap tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset tw-ring-iron-600">All submissions</div>{curationGroups.map(_temp11)}</div><div ref={curationDropdownProbeRef} className="tailwind-scope tw-inline-flex tw-whitespace-nowrap tw-rounded-lg tw-py-2.5 tw-pl-3.5 tw-pr-8 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset tw-ring-iron-700"><span className="tw-font-semibold tw-text-iron-500">Group: </span>{activeCurationLabel}</div></>;
    $[109] = activeCurationLabel;
    $[110] = curationDropdownProbeRef;
    $[111] = curationGroups;
    $[112] = curationTabsProbeRef;
    $[113] = showCurationGroupSelect;
    $[114] = t42;
  } else {
    t42 = $[114];
  }
  let t43;
  if ($[115] !== actionsFullProbeRef || $[116] !== actionsIconProbeRef || $[117] !== showCreateAction || $[118] !== showCurationActions) {
    t43 = showCurationActions && <><div ref={actionsFullProbeRef} className="tw-inline-flex tw-items-center tw-gap-2"><div className="tw-inline-flex tw-h-9 tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-px-3.5 tw-text-xs tw-font-semibold tw-text-iron-200"><AdjustmentsHorizontalIcon className="tw-size-4 tw-flex-shrink-0" /><span>Filters</span></div>{showCreateAction && <div className="tw-inline-flex tw-items-center tw-justify-center tw-gap-x-1.5 tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-bg-iron-200 tw-px-3.5 tw-py-2 tw-text-sm tw-font-semibold tw-text-iron-950 tw-ring-1 tw-ring-inset tw-ring-white"><PlusIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /><span>Drop Art</span></div>}</div><div ref={actionsIconProbeRef} className="tw-inline-flex tw-items-center tw-gap-2"><div className="tw-inline-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-text-xs tw-font-semibold tw-text-iron-200"><AdjustmentsHorizontalIcon className="tw-size-4 tw-flex-shrink-0" /></div>{showCreateAction && <div className="tw-inline-flex tw-items-center tw-justify-center tw-gap-x-1.5 tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-bg-iron-200 tw-px-2.5 tw-py-2 tw-text-sm tw-font-semibold tw-text-iron-950 tw-ring-1 tw-ring-inset tw-ring-white"><DropModeGlyphIcon /></div>}</div></>;
    $[115] = actionsFullProbeRef;
    $[116] = actionsIconProbeRef;
    $[117] = showCreateAction;
    $[118] = showCurationActions;
    $[119] = t43;
  } else {
    t43 = $[119];
  }
  let t44;
  if ($[120] !== t39 || $[121] !== t41 || $[122] !== t42 || $[123] !== t43) {
    t44 = <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-0 tw-top-0 tw-h-0 tw-overflow-hidden tw-whitespace-nowrap tw-opacity-0">{t39}{t41}{t42}{t43}</div>;
    $[120] = t39;
    $[121] = t41;
    $[122] = t42;
    $[123] = t43;
    $[124] = t44;
  } else {
    t44 = $[124];
  }
  let t45;
  if ($[125] !== t36 || $[126] !== t37 || $[127] !== t44) {
    t45 = <div className={t21}>{t36}{t37}{t44}</div>;
    $[125] = t36;
    $[126] = t37;
    $[127] = t44;
    $[128] = t45;
  } else {
    t45 = $[128];
  }
  return t45;
};
function _temp() {}
function _temp2() {
  return false;
}
function _temp3(item) {
  return [item.value, item.label];
}
function _temp4(group_0) {
  return `${group_0.id}:${group_0.name}`;
}
function _temp5(mode) {
  if (mode === "list") {
    return "List view";
  }
  if (mode === "grid") {
    return "Grid view";
  }
  return "Content only";
}
function _temp6(mode_0, activeMode) {
  if (activeMode === mode_0) {
    return "tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-transparent tw-transition-colors tw-border-primary-500/50 tw-bg-primary-600/20 tw-text-primary-400";
  }
  return "tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-md tw-border tw-border-solid tw-border-transparent tw-transition-colors tw-bg-transparent tw-text-iron-500 desktop-hover:hover:tw-bg-white/5 desktop-hover:hover:tw-text-iron-300";
}
function _temp7(mode_1) {
  if (mode_1 === "list") {
    return <svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="tw-size-5 tw-flex-shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" /></svg>;
  }
  if (mode_1 === "grid") {
    return <svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="tw-size-5 tw-flex-shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" /></svg>;
  }
  return <svg xmlns="http://www.w3.org/2000/svg" fill="none" aria-hidden="true" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="tw-size-5 tw-flex-shrink-0"><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h12A2.25 2.25 0 0 1 20.25 6v12A2.25 2.25 0 0 1 18 20.25H6A2.25 2.25 0 0 1 3.75 18V6Zm3.75 3h9m-9 4.5h9m-9 4.5h5.25" /></svg>;
}
function _temp8(current) {
  return !current;
}
function _temp9(current_0) {
  return !current_0;
}
function _temp10(item_0) {
  return <div key={item_0.key} className="tw-flex tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold">{item_0.label}</div>;
}
function _temp11(group_1) {
  return <div key={group_1.id} className="tw-flex tw-items-center tw-gap-x-1.5 tw-whitespace-nowrap tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset tw-ring-iron-600">{group_1.name}</div>;
}